import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useProfiles } from "../profiles/ProfileContext";
import { Button } from "../components/ui/Button";
import { PageHeader } from "../components/Surface";
import { Icon } from "../app/Icon";
import { FormRow, Select, CheckboxGroup, inputClass } from "../components/FormKit";
import { PROFILE_TYPE_LABELS } from "../lib/profiles";
import { Paywall, usePaywallGate } from "../components/Paywall";
import { AiAssist } from "../components/AiAssist";
import { listMyOrgs, orgCan, type Org } from "../lib/orgs";
import {
  createJob, HIRING_TYPES,
  EMPLOYMENT_TYPE_OPTIONS, ROUTE_TYPE_OPTIONS, HOME_TIME_OPTIONS, PAY_TYPE_OPTIONS, CDL_CLASS_OPTIONS,
  ENDORSEMENT_OPTIONS, BENEFIT_OPTIONS, JOB_EQUIPMENT_OPTIONS, FREIGHT_OPTIONS,
} from "../lib/jobs";

const DRAFT_KEY = "rt.draft.job";

export function PostJob() {
  const { active } = useProfiles();
  const [paywall, dismissPaywall] = usePaywallGate();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [postOrg, setPostOrg] = useState("");
  useEffect(() => {
    listMyOrgs().then((r) => setOrgs(r.organizations.filter((o) => orgCan(o.roles, "jobs.post")))).catch(() => undefined);
  }, []);
  const navigate = useNavigate();
  const [f, setF] = useState<Record<string, string>>({ employment_type: "company_driver", pay_type: "annual" });
  const [equipment, setEquipment] = useState<string[]>([]);
  const [freight, setFreight] = useState<string[]>([]);
  const [endorsements, setEndorsements] = useState<string[]>([]);
  const [benefits, setBenefits] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) ?? "null");
      if (d && d.f) {
        setF(d.f);
        setEquipment(d.equipment ?? []);
        setFreight(d.freight ?? []);
        setEndorsements(d.endorsements ?? []);
        setBenefits(d.benefits ?? []);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const set = (k: string) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const setE = (k: string) => (e: { target: { value: string } }) => set(k)(e.target.value);
  const toggle = (setter: React.Dispatch<React.SetStateAction<string[]>>) => (v: string) =>
    setter((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));
  const num = (v: string) => (v ? Number(v) : undefined);
  const isHiring = active ? HIRING_TYPES.includes(active.type) : false;

  function saveDraft() {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ f, equipment, freight, endorsements, benefits }));
      setNote("Draft saved on this device.");
    } catch {
      setNote("Could not save the draft.");
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!active) return;
    setBusy(true);
    setError(null);
    try {
      const { job } = await createJob({
        poster_profile_id: active.id,
        org_id: postOrg || undefined,
        title: f.title,
        description: f.description || undefined,
        location: f.location || undefined,
        employment_type: f.employment_type || undefined,
        openings: num(f.openings),
        route_type: f.route_type || undefined,
        home_time: f.home_time || undefined,
        avg_miles_week: num(f.avg_miles_week),
        pay_type: f.pay_type || undefined,
        pay_min: num(f.pay_min),
        pay_max: num(f.pay_max),
        sign_on_bonus: num(f.sign_on_bonus),
        cdl_class: f.cdl_class || undefined,
        experience_months: num(f.experience_months),
        endorsements,
        equipment,
        freight_types: freight,
        benefits,
      });
      localStorage.removeItem(DRAFT_KEY);
      navigate(`/jobs/${job.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not post the job");
    } finally {
      setBusy(false);
    }
  }

  const payHint =
    f.pay_type === "cpm" ? "Suggested range: 45–75 (cents per mile)" :
    f.pay_type === "hourly" ? "Suggested range: $22–$32 per hour" :
    f.pay_type === "percentage" ? "Typical: 65–75% of load" : "Suggested range: $55k–$85k per year";
  const title = f.title ?? "";
  const desc = f.description ?? "";

  return (
    <div className="grid gap-6">
      {paywall && <Paywall onClose={dismissPaywall} />}
      <PageHeader title="Post a job" subtitle="Create a new driver job posting. Add details to attract qualified drivers." />

      {!isHiring ? (
        <p className="text-sm text-muted">Switch to a company, recruiter, or broker/dispatcher profile to post a job.</p>
      ) : (
        <form onSubmit={onSubmit}>
          <div className="rounded-card border border-line bg-surface px-5 sm:px-6">
            {orgs.length > 0 && (
              <FormRow label="Post as" hint="Post personally or on behalf of an organization.">
                <select className={inputClass} value={postOrg} onChange={(e) => setPostOrg(e.target.value)} aria-label="Post as">
                  <option value="">Personal ({active?.display_name ?? "me"})</option>
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </FormRow>
            )}
            <FormRow label="Title / Role" hint="What role are you hiring for?" required>
              <input className={inputClass} aria-label="Job title" placeholder="Reefer Driver" maxLength={100} value={title} onChange={setE("title")} required />
              <span className="justify-self-end font-mono text-[11px] text-muted">{title.length}/100</span>
            </FormRow>

            <FormRow label="Employment" hint="How the driver is engaged.">
              <Select label="" value={f.employment_type ?? ""} onChange={set("employment_type")} options={EMPLOYMENT_TYPE_OPTIONS} ariaLabel="Employment type" />
            </FormRow>

            <FormRow label="Route" hint="Lane pattern and home time.">
              <div className="grid gap-3 sm:grid-cols-2">
                <Select label="" value={f.route_type ?? ""} onChange={set("route_type")} options={ROUTE_TYPE_OPTIONS} placeholder="Route type" ariaLabel="Route type" />
                <Select label="" value={f.home_time ?? ""} onChange={set("home_time")} options={HOME_TIME_OPTIONS} placeholder="Home time" ariaLabel="Home time" />
              </div>
            </FormRow>

            <FormRow label="Miles & openings" hint="Weekly miles and how many seats.">
              <div className="grid gap-3 sm:grid-cols-2">
                <input className={inputClass} type="number" min="0" placeholder="Avg miles / week" value={f.avg_miles_week ?? ""} onChange={setE("avg_miles_week")} />
                <input className={inputClass} type="number" min="1" placeholder="Openings" value={f.openings ?? ""} onChange={setE("openings")} />
              </div>
            </FormRow>

            <FormRow label="Pay" hint="Set the pay type, range, and bonus." required>
              <Select label="" value={f.pay_type ?? ""} onChange={set("pay_type")} options={PAY_TYPE_OPTIONS} ariaLabel="Pay type" />
              <div className="grid gap-3 sm:grid-cols-3">
                <input className={inputClass} type="number" min="0" aria-label="Pay min" placeholder="Pay min" value={f.pay_min ?? ""} onChange={setE("pay_min")} />
                <input className={inputClass} type="number" min="0" aria-label="Pay max" placeholder="Pay max" value={f.pay_max ?? ""} onChange={setE("pay_max")} />
                <input className={inputClass} type="number" min="0" aria-label="Sign-on bonus" placeholder="Sign-on bonus $" value={f.sign_on_bonus ?? ""} onChange={setE("sign_on_bonus")} />
              </div>
              <span className="font-mono text-[11px] text-muted">{payHint}</span>
            </FormRow>

            <FormRow label="Requirements" hint="CDL class and minimum experience.">
              <div className="grid gap-3 sm:grid-cols-2">
                <Select label="" value={f.cdl_class ?? ""} onChange={set("cdl_class")} options={CDL_CLASS_OPTIONS} placeholder="CDL class" ariaLabel="CDL class" />
                <input className={inputClass} type="number" min="0" placeholder="Min experience (months)" value={f.experience_months ?? ""} onChange={setE("experience_months")} />
              </div>
            </FormRow>

            <FormRow label="Endorsements" hint="Select required endorsements.">
              <CheckboxGroup label="" options={ENDORSEMENT_OPTIONS} selected={endorsements} onToggle={toggle(setEndorsements)} />
            </FormRow>

            <FormRow label="Equipment" hint="Select the equipment type.">
              <CheckboxGroup label="" options={JOB_EQUIPMENT_OPTIONS} selected={equipment} onToggle={toggle(setEquipment)} />
            </FormRow>

            <FormRow label="Freight" hint="Freight the driver will haul.">
              <CheckboxGroup label="" options={FREIGHT_OPTIONS} selected={freight} onToggle={toggle(setFreight)} />
            </FormRow>

            <FormRow label="Benefits" hint="Benefits offered.">
              <CheckboxGroup label="" options={BENEFIT_OPTIONS} selected={benefits} onToggle={toggle(setBenefits)} />
            </FormRow>

            <FormRow label="Work location" hint="Where is the job based?">
              <div className="relative flex items-center">
                <Icon name="pin" className="pointer-events-none absolute left-3 h-4 w-4 text-muted" />
                <input className={`w-full ${inputClass} pl-9`} aria-label="Location" placeholder="Dallas, TX" value={f.location ?? ""} onChange={setE("location")} />
              </div>
            </FormRow>

            <FormRow label="Description" hint="Describe the job, requirements, and what drivers can expect." required>
              <textarea className={inputClass} rows={6} maxLength={2000} placeholder="Route, freight, expectations…" value={desc} onChange={setE("description")} />
              <div className="flex items-center justify-between gap-2">
                <AiAssist kind="job_description" value={desc} onResult={(t) => set("description")(t.slice(0, 2000))} label="Draft with AI" />
                <span className="font-mono text-[11px] text-muted">{desc.length}/2000</span>
              </div>
            </FormRow>

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t-2 border-dashed border-accent/40 py-5">
              <p className="flex flex-wrap items-center gap-2 text-sm text-muted">
                <Icon name="verify" className="h-4 w-4 text-verified" />
                Publishing requires your {active ? PROFILE_TYPE_LABELS[active.type] : "hiring"} profile to be verified.{" "}
                <Link to="/verify" className="font-bold text-accent-strong">Verify now →</Link>
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {error && <span className="font-mono text-sm text-danger">{error}</span>}
                {note && <span className="font-mono text-sm text-muted">{note}</span>}
                <Button variant="ghost" type="button" onClick={saveDraft}>Save draft</Button>
                <Button type="submit" disabled={busy || !f.title}>{busy ? "Publishing…" : "Publish"}</Button>
              </div>
            </div>
          </div>

          <Link to="/jobs" className="mt-3 inline-block text-sm font-bold text-muted">Cancel</Link>
        </form>
      )}
    </div>
  );
}
